import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useRef, useEffect, useCallback, forwardRef, useContext } from "react";
import { motion, usePresence, AnimatePresence } from "motion/react";
import { Countdown$1 } from "../../chunks/index.es.js";
import { formatIntlUSD } from "../../chunks/utils.js";
import { CountUp } from "../../chunks/index.js";
import { LoggerContext } from "../../chunks/logger.context.js";
import { Color } from "../../common/enums/colors.js";
import './donation-train.overlay.css';function DonationTrainDonorName({
  children,
  donations
}) {
  const content = useMemo(() => {
    return React.Children.toArray(children);
  }, [children]);
  const [currentDonation, setCurrentDonation] = useState(0);
  const indexRef = useRef(currentDonation);
  const contentRef = useRef(content[currentDonation]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentDonation < donations.length - 1) {
        setCurrentDonation(currentDonation + 1);
      } else {
        setCurrentDonation(0);
      }
    }, 5e3);
    return () => clearInterval(interval);
  });
  useEffect(() => {
    if (indexRef.current === currentDonation) return () => {
    };
    if (contentRef.current === content[currentDonation]) return () => {
    };
    const timeout = setTimeout(() => {
      indexRef.current = currentDonation;
      contentRef.current = content[indexRef.current];
    }, 250);
    return () => {
      clearTimeout(timeout);
    };
  }, [content, currentDonation]);
  const variants = {
    exit: { x: 30, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    initial: { x: -30, opacity: 0 }
  };
  return /* @__PURE__ */ jsx(Fragment, { children: content.map((innerContent, index) => {
    const key = index;
    return currentDonation === key && /* @__PURE__ */ jsx(
      motion.div,
      {
        variants,
        initial: "initial",
        animate: "animate",
        exit: "exit",
        children: innerContent
      },
      key
    );
  }) });
}
function DonationTrainCountdown({
  trainID,
  trainEnd,
  onTrainEnded,
  onTrainEnding
}) {
  const [willEndSoon, setWillEndSoon] = useState(false);
  const isEnding = useRef(false);
  const parsedTrainEnd = useMemo(() => {
    return new Date(trainEnd);
  }, [trainEnd]);
  useEffect(() => {
    if (!isEnding.current) {
      const now = /* @__PURE__ */ new Date();
      if (parsedTrainEnd.getTime() < now.getTime()) {
        isEnding.current = true;
        onTrainEnded == null ? void 0 : onTrainEnded(trainID);
      }
    }
  }, [onTrainEnded, parsedTrainEnd, trainID]);
  const countdownRenderer = ({ formatted, minutes }) => {
    return /* @__PURE__ */ jsxs("span", { children: [
      minutes,
      ":",
      formatted.seconds
    ] });
  };
  const onTick = (value) => {
    if (value.minutes === 0 && value.seconds < 60 && !willEndSoon) {
      setWillEndSoon(true);
    }
    if (value.minutes === 0 && value.seconds < 2 && !isEnding.current) {
      isEnding.current = true;
      setTimeout(() => {
        onTrainEnding == null ? void 0 : onTrainEnding(trainID);
      }, 2e3);
    }
    if (value.minutes > 0 && willEndSoon) {
      setWillEndSoon(false);
    }
  };
  const onComplete = () => {
    if (!isEnding.current) {
      onTrainEnding == null ? void 0 : onTrainEnding(trainID);
    }
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: willEndSoon ? "donation-train__counter train-ending" : "donation-train__counter",
      children: /* @__PURE__ */ jsx("h1", { children: /* @__PURE__ */ jsx(
        Countdown$1,
        {
          onComplete,
          onTick,
          date: parsedTrainEnd,
          renderer: countdownRenderer
        }
      ) })
    }
  );
}
function DonationTrainValue({
  donationValue,
  removeTrailingZeros = true,
  backgroundColor,
  status = "active"
}) {
  return (
    // <div className="w-28 flex flex-col items-center justify-center value-wrapper">
    //   <h1 className="font-bold text-2xl text-white">
    //     {formatIntlUSD(donationValue, removeTrailingZeros)}
    //   </h1>
    //   <span className="text-xs font-normal text-gray-400 value-label">
    //     Train Amount
    //   </span>
    // </div>
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: `donation-train__value ${backgroundColor} ${status.toLowerCase()}`,
        children: [
          /* @__PURE__ */ jsx("h1", { children: formatIntlUSD(donationValue, removeTrailingZeros) }),
          /* @__PURE__ */ jsx("span", { children: "Train Amount" })
        ]
      }
    )
  );
}
function DonationTrainCounter({ count }) {
  const startRef = useRef(count);
  const onEnd = useCallback(() => {
    startRef.current = count;
  }, [count]);
  useEffect(() => {
    setTimeout(() => {
      startRef.current = count;
    }, 250);
  }, [count]);
  return /* @__PURE__ */ jsxs("div", { className: "donation-train__facts_left", children: [
    /* @__PURE__ */ jsx(CountUp, { onEnd, end: count, start: startRef.current, children: ({ countUpRef }) => /* @__PURE__ */ jsx("h1", { children: /* @__PURE__ */ jsx("span", { ref: countUpRef }) }) }),
    /* @__PURE__ */ jsxs("span", { children: [
      " Donation",
      count === 1 ? "" : "s"
    ] })
  ] });
}
function DonationTrainFacts({
  donations,
  donationValue,
  trainStart,
  trainEnd,
  trainID,
  onFactChange
}) {
  const raisedAmount = useMemo(() => {
    const amount = donations.length * Number(donationValue);
    return formatIntlUSD(amount);
  }, [donationValue, donations.length]);
  const trainDuration = useMemo(() => {
    const difference = new Date(trainEnd || /* @__PURE__ */ new Date()).getTime() - new Date(trainStart).getTime();
    return Math.round(difference / 6e4);
  }, [trainStart, trainEnd]);
  const slides = useMemo(() => {
    const donorOne = donations[0];
    const donorTwo = donations[1];
    const donorThree = donations[2];
    const donorFour = donations[3];
    const initialItems = [
      /* @__PURE__ */ jsx("h1", { className: "texfact", children: "Train has ended" }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx("span", { children: "Total Donations" }),
        /* @__PURE__ */ jsx("h1", { children: donations.length })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx("span", { children: "Amount Raised" }),
        /* @__PURE__ */ jsx("h1", { children: raisedAmount })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx("span", { children: "Train Duration" }),
        /* @__PURE__ */ jsxs("h1", { children: [
          trainDuration,
          " minutes"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx("span", { children: "Train Started By" }),
        /* @__PURE__ */ jsx("h1", { children: donorOne.donorName })
      ] })
    ];
    if (donorTwo) {
      initialItems.push(
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { children: "Train Started By" }),
          /* @__PURE__ */ jsx("h1", { children: donorTwo.donorName })
        ] })
      );
    }
    if (donorThree) {
      initialItems.push(
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { children: "Train Started By" }),
          /* @__PURE__ */ jsx("h1", { children: donorThree.donorName })
        ] })
      );
    }
    if (donorFour) {
      initialItems.push(
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { children: "Train Started By" }),
          /* @__PURE__ */ jsx("h1", { children: donorFour.donorName })
        ] })
      );
    }
    return [...initialItems, /* @__PURE__ */ jsx("h1", { children: "Thank you!" })];
  }, [donations, raisedAmount, trainDuration]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const indexRef = useRef(currentSlide);
  const contentRef = useRef(slides[currentSlide]);
  useEffect(() => {
    if (indexRef.current === currentSlide) return () => {
    };
    if (contentRef.current === slides[currentSlide]) return () => {
    };
    const timeout = setTimeout(() => {
      indexRef.current = currentSlide;
      contentRef.current = slides[indexRef.current];
    }, 250);
    return () => {
      clearTimeout(timeout);
    };
  }, [slides, currentSlide]);
  useEffect(() => {
    const interval = setInterval(() => {
      let nextSlide = 0;
      if (currentSlide < slides.length - 1) {
        nextSlide = currentSlide + 1;
      }
      setCurrentSlide(nextSlide);
      onFactChange == null ? void 0 : onFactChange({ trainID, index: nextSlide });
    }, 5e3);
    return () => clearInterval(interval);
  });
  return /* @__PURE__ */ jsx("div", { className: "donation-train__content ending", children: slides.map((innerContent, index) => {
    const key = index;
    return currentSlide === key && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, x: -10 },
        animate: { opacity: 1, x: 0 },
        transition: {
          duration: 1
        },
        children: innerContent
      },
      key
    );
  }) });
}
function DonationTrainTotal({
  donationCount,
  donationValue
}) {
  const result = useMemo(() => {
    return donationCount * Number(donationValue);
  }, [donationCount, donationValue]);
  const startRef = useRef(donationCount * Number(donationValue));
  const onEnd = useCallback(() => {
    startRef.current = result;
  }, [result]);
  useEffect(() => {
    setTimeout(() => {
      startRef.current = result;
    }, 250);
  }, [result]);
  const totalStyles = {
    width: `${80 + (result.toString().length - 3) * 10}px`,
    marginRight: result.toString().length >= 4 ? "0.5rem" : "",
    marginLeft: result.toString().length >= 4 ? "0.5rem" : ""
  };
  return /* @__PURE__ */ jsx(
    CountUp,
    {
      onEnd,
      end: result,
      start: startRef.current,
      separator: ",",
      prefix: "$",
      children: ({ countUpRef }) => /* @__PURE__ */ jsxs(
        "div",
        {
          className: "donation-train__facts_right",
          style: totalStyles,
          children: [
            /* @__PURE__ */ jsx("h1", { children: /* @__PURE__ */ jsx("span", { ref: countUpRef }) }),
            /* @__PURE__ */ jsx("span", { children: "Raised" })
          ]
        }
      )
    }
  );
}
function DonationTrainPending({
  minDonations = 5,
  currentDonations = 0
}) {
  const donationsToGo = useMemo(() => {
    return minDonations - currentDonations;
  }, [minDonations, currentDonations]);
  return /* @__PURE__ */ jsx("div", { className: "donation-train__content", children: /* @__PURE__ */ jsx(
    motion.div,
    {
      animate: {
        textShadow: ["0 0 10px #fff", "0 0 10px #000"]
      },
      transition: {
        duration: 0.8,
        // ease: 'easeInOut',
        repeat: Infinity,
        repeatType: "reverse"
        // repeatDelay: 10,
      },
      children: /* @__PURE__ */ jsxs("h1", { className: "text-2xl text-white mr-6", children: [
        donationsToGo.toString(),
        " donation",
        donationsToGo === 1 ? "" : "s",
        " ",
        "needed to activate"
      ] })
    }
  ) });
}
const BASE_SLIDE_COUNT = 5;
const calculateSlideCount = (donationCount) => {
  if (donationCount >= 0 && donationCount <= 4) {
    return BASE_SLIDE_COUNT + donationCount;
  }
  return 9;
};
const DonationTrainItem = forwardRef(function DonationTrainItem2(props, ref) {
  var _a;
  const {
    train,
    onTrainEnded,
    onTrainEnding,
    key,
    removeTrailingZeros = true,
    backgroundColor,
    onFactChange
  } = props;
  const [isPresent, safeToRemove] = usePresence();
  const didEndRef = useRef(false);
  const isEndingRef = useRef(false);
  const statusRef = useRef(train.trainStatus);
  useEffect(() => {
    if (statusRef.current === "ENDING" && train.trainStatus === "ACTIVE") {
      statusRef.current = "ACTIVE";
      isEndingRef.current = false;
      didEndRef.current = false;
    } else if (statusRef.current === "ACTIVE" && train.trainStatus === "ENDING") {
      statusRef.current = "ENDING";
    } else if (statusRef.current === "PENDING" && train.trainStatus === "ACTIVE") {
      statusRef.current = "ACTIVE";
    } else if (statusRef.current === "ENDING" && train.trainStatus === "ENDED") {
      statusRef.current = "ENDED";
    }
  }, [train.trainStatus]);
  useEffect(() => {
    if (!isPresent) setTimeout(safeToRemove, 500);
  }, [isPresent, safeToRemove]);
  const slideCount = useMemo(() => {
    return calculateSlideCount(train.donationCount);
  }, [train.donationCount]);
  const trainEnded = useCallback(() => {
    if (!didEndRef.current) {
      didEndRef.current = true;
      onTrainEnded == null ? void 0 : onTrainEnded(train.id);
    }
  }, [onTrainEnded, train.id]);
  const trainEnding = useCallback(() => {
    if (!isEndingRef.current) {
      isEndingRef.current = true;
      onTrainEnding == null ? void 0 : onTrainEnding(train.id);
    }
  }, [onTrainEnding, train.id]);
  const handleTrainEnding = useCallback(() => {
    const timeout = setTimeout(() => {
      trainEnded();
    }, slideCount * 5e3);
    trainEnding();
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [slideCount, trainEnding, trainEnded]);
  const formatName = useCallback((name) => {
    if (name.length > 11) {
      return `${name.slice(0, 17)}...`;
    }
    return name;
  }, []);
  const showFacts = useMemo(() => {
    return train.trainStatus === "ENDING";
  }, [train.trainStatus]);
  const growStyles = {
    width: `${train.trainStatus === "PENDING" || train.trainStatus === "ENDING" ? `${0}%` : `${100}%`}`
  };
  return /* @__PURE__ */ jsx("div", { className: "donation-train", ref, children: /* @__PURE__ */ jsxs(
    motion.div,
    {
      initial: { opacity: 0, x: -10 },
      animate: { opacity: 1, x: 0 },
      transition: {
        duration: 1
      },
      className: "donation-train__wrapper",
      children: [
        /* @__PURE__ */ jsx(
          DonationTrainValue,
          {
            donationValue: train.donationValue,
            removeTrailingZeros,
            status: train.trainStatus,
            backgroundColor
          }
        ),
        train.trainStatus === "PENDING" && /* @__PURE__ */ jsx(
          DonationTrainPending,
          {
            currentDonations: train.donationCount,
            minDonations: ((_a = train.ruleset) == null ? void 0 : _a.donationsCountMin) ?? 5
          }
        ),
        train.trainStatus === "ACTIVE" && /* @__PURE__ */ jsxs(
          motion.div,
          {
            initial: { opacity: 0, x: -10 },
            animate: { opacity: 1, x: 0 },
            transition: {
              duration: 0.8,
              delay: 0.3
            },
            style: { width: "578px" },
            children: [
              !showFacts && train.trainEnd && /* @__PURE__ */ jsx(
                "div",
                {
                  className: `donation-train__content ${backgroundColor} ${train.trainStatus.toLowerCase()}`,
                  children: /* @__PURE__ */ jsx(DonationTrainDonorName, { donations: train.donations, children: train.donations.map((donation) => /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx("h1", { children: formatName(donation.donorName) }) }, donation.id)) })
                }
              ),
              !showFacts && train.trainEnd && /* @__PURE__ */ jsxs("div", { className: "donation-train__facts", children: [
                /* @__PURE__ */ jsx(DonationTrainCounter, { count: train.donations.length }),
                /* @__PURE__ */ jsx("div", { className: "donation-train__facts_middle" }),
                /* @__PURE__ */ jsx(
                  DonationTrainTotal,
                  {
                    donationCount: train.donations.length,
                    donationValue: train.donationValue
                  }
                )
              ] }),
              !showFacts && train.trainEnd && /* @__PURE__ */ jsx("div", { children: train.trainEnd && /* @__PURE__ */ jsx(
                DonationTrainCountdown,
                {
                  trainID: train.id,
                  trainEnd: train.trainEnd,
                  onTrainEnded: () => {
                    trainEnded();
                  },
                  onTrainEnding: handleTrainEnding
                }
              ) })
            ]
          }
        ),
        train.trainStatus === "ENDING" && showFacts && train.trainEnd && /* @__PURE__ */ jsx(
          DonationTrainFacts,
          {
            trainID: train.id,
            trainStart: train.trainStart || /* @__PURE__ */ new Date(),
            trainEnd: train.trainEnd,
            donations: train.donations,
            onFactChange,
            donationValue: train.donationValue
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: `donation-train__bg ${backgroundColor}`,
            style: growStyles
          }
        )
      ]
    },
    key
  ) });
});
function DonationTrainOverlay({
  trains = [],
  backgroundColor = Color.DEFAULT_RED,
  onTrainEnded,
  onTrainEnding,
  theme = "regular",
  removeTrailingZeros = true,
  demoMode = false,
  maxTrainsVisible = Number.MAX_SAFE_INTEGER,
  onTrainsChanged,
  onFactChange,
  showActivationMessage = true,
  position = "top"
}) {
  const [currentTrains, setCurrentTrains] = useState(trains);
  const trainsRef = useRef(trains);
  const logger = useContext(LoggerContext);
  const endedTrainsRef = useRef(/* @__PURE__ */ new Map());
  const endingTrainsRef = useRef(/* @__PURE__ */ new Map());
  const sortTrains = useCallback((a, b) => {
    if (a.trainStatus === "PENDING" && b.trainStatus !== "PENDING") return -1;
    if (a.trainStatus === "ENDING" || a.trainStatus === "ENDED") return -1;
    if (b.trainStatus === "ENDING" || b.trainStatus === "ENDED") return 1;
    if (a.trainStart && b.trainStart) {
      if (new Date(a.trainStart) < new Date(b.trainStart)) return 1;
      if (new Date(b.trainStart) < new Date(a.trainStart)) return -1;
    }
    return 0;
  }, []);
  const filterTrains = useCallback(
    (train) => {
      if (["ACTIVE", "ENDING"].includes(train.trainStatus)) return true;
      if (train.trainStatus === "ENDED") return false;
      if (train.trainStatus === "PENDING") {
        if (train.ruleset) {
          return train.ruleset.donationsCountMin - train.donationCount <= 2;
        }
        return train.donationCount >= 2;
      }
      return !demoMode;
    },
    [demoMode]
  );
  const handleTrainEnding = useCallback(
    (trainID) => {
      logger.log(`Train ending`, trainID);
      if (!endingTrainsRef.current.has(trainID)) {
        onTrainEnding == null ? void 0 : onTrainEnding(trainID);
        endingTrainsRef.current.set(trainID, true);
      }
    },
    [logger, onTrainEnding]
  );
  const handleDemoMode = useCallback(
    (trainID) => {
      const trainIndex = trainsRef.current.findIndex((t) => t.id === trainID);
      if (trainIndex > -1) {
        const newTrains = [...trainsRef.current];
        const updatedTrain = newTrains[trainIndex];
        if (updatedTrain.trainStatus === "ACTIVE") {
          updatedTrain.trainStatus = "ENDING";
        } else if (updatedTrain.trainStatus === "ENDING") {
          updatedTrain.trainStatus = "ENDED";
        }
        newTrains[trainIndex] = updatedTrain;
        const filteredTrains = trains.filter(filterTrains);
        trainsRef.current = newTrains;
        const slicedAndSorted = filteredTrains.sort(sortTrains).slice(0, maxTrainsVisible);
        setCurrentTrains(slicedAndSorted);
        if (onTrainsChanged) onTrainsChanged(slicedAndSorted);
      }
    },
    [trains, filterTrains, sortTrains, maxTrainsVisible, onTrainsChanged]
  );
  const handleTrainEnded = useCallback(
    (trainID) => {
      logger.log(`Train ended`, trainID);
      if (demoMode) {
        handleDemoMode(trainID);
      }
      if (!endedTrainsRef.current.has(trainID)) {
        onTrainEnded == null ? void 0 : onTrainEnded(trainID);
        endedTrainsRef.current.set(trainID, true);
      }
    },
    [onTrainEnded, logger, demoMode, handleDemoMode]
  );
  useEffect(() => {
    const filteredTrains = trains.filter(filterTrains);
    trainsRef.current = filteredTrains;
    const filterTrain = (train) => {
      if (train.trainStatus === "PENDING") {
        if (train.ruleset) {
          return train.ruleset.donationsCountMin - train.donationCount <= 2;
        }
        return train.donationCount >= 2;
      }
      return train.trainStatus === "ACTIVE" && train.trainEnd;
    };
    if (!demoMode) {
      trains.filter(filterTrain).forEach((train) => {
        const threshold = calculateSlideCount(train.donationCount) * 5e3;
        const now = /* @__PURE__ */ new Date();
        now.setMilliseconds(0);
        const trainEnd = new Date(train.trainEnd);
        trainEnd.setMilliseconds(0);
        if (trainEnd > now) return;
        if (now.getTime() - trainEnd.getTime() > threshold) {
          logger.log("Train ended", train.id);
          if (!endedTrainsRef.current.has(train.id)) {
            onTrainEnded == null ? void 0 : onTrainEnded(train.id);
            endedTrainsRef.current.set(train.id, true);
          }
        } else if (now.getTime() - trainEnd.getTime() < 1e3) {
          logger.log("Train ending", train.id);
          if (!endingTrainsRef.current.has(train.id)) {
            onTrainEnding == null ? void 0 : onTrainEnding(train.id);
            endingTrainsRef.current.set(train.id, true);
          }
        }
      });
    }
    const slicedAndSorted = filteredTrains.sort(sortTrains).slice(0, maxTrainsVisible);
    setCurrentTrains(slicedAndSorted);
  }, [
    demoMode,
    logger,
    trains,
    filterTrains,
    maxTrainsVisible,
    sortTrains,
    onTrainEnding,
    onTrainEnded
  ]);
  const className = useMemo(() => {
    const baseClassName = `donation-train-overlay ${position}`;
    if (theme === "regular") {
      return baseClassName;
    }
    if (theme === "new") {
      return `${baseClassName} new`;
    }
    return `${baseClassName} bac`;
  }, [position, theme]);
  const emptyContent = useMemo(() => {
    return showActivationMessage ? /* @__PURE__ */ jsx("div", { className: "donation-train", children: /* @__PURE__ */ jsx("div", { className: "donation-train__wrapper", children: /* @__PURE__ */ jsx(AnimatePresence, { children: /* @__PURE__ */ jsx(
      motion.div,
      {
        animate: {
          x: [1, -1, 1, -1, 1, -1]
        },
        transition: {
          duration: 0.5,
          ease: "easeInOut",
          repeat: Infinity,
          delay: 10,
          repeatDelay: 10
        },
        className: "donation-train__content ended",
        children: /* @__PURE__ */ jsx("h1", { children: "Donate now to activate the donation train!" })
      }
    ) }) }) }) : /* @__PURE__ */ jsx("div", {});
  }, [showActivationMessage]);
  return /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx("div", { className: "trains-wrapper", children: currentTrains.length < 1 ? emptyContent : /* @__PURE__ */ jsx(AnimatePresence, { mode: "popLayout", children: currentTrains.map((train) => /* @__PURE__ */ jsx(
    DonationTrainItem,
    {
      onTrainEnding: handleTrainEnding,
      backgroundColor,
      removeTrailingZeros,
      onTrainEnded: handleTrainEnded,
      train,
      onFactChange
    },
    train.id
  )) }) }) });
}
const DonationTrainOverlayBackgroundColors = [
  Color.DEFAULT_RED,
  Color.LIGHT_PURPLE,
  Color.DARK_PURPLE,
  Color.LIGHT_BLUE,
  Color.DARK_BLUE,
  Color.LIGHT_AQUA,
  Color.LIGHT_GREEN,
  Color.DARK_GREEN,
  Color.LIGHT_YELLOW,
  Color.DARK_YELLOW
];
function toDonationTrainOverlayBackgroundColor(color) {
  if (!color) return Color.DEFAULT_RED;
  return DonationTrainOverlayBackgroundColors.find((c) => c === color) ?? Color.DEFAULT_RED;
}
function getDonationTrainConfigFromURL(url) {
  const config = new URLSearchParams(url.search);
  const theme = (config.get("theme") ?? "regular") === "regular" ? "regular" : "bac";
  const position = (config.get("position") ?? "top") === "top" ? "top" : "bottom";
  const showAllDonations = (config.get("showAllDonations") ?? "no") === "yes";
  const removeTrailingZeros = (config.get("removeTrailingZeros") ?? "yes") === "yes";
  const backgroundColor = toDonationTrainOverlayBackgroundColor(
    config.get("bgColor")
  );
  const showActivationMessage = (config.get("showActivationMessage") ?? "yes") === "yes";
  const demoMode = (config.get("demoMode") ?? "no") === "yes";
  const maxTrainsVisible = Number.parseInt(
    config.get("maxTrainsVisible") ?? "1",
    10
  );
  return {
    theme,
    position,
    maxTrainsVisible,
    showAllDonations,
    removeTrailingZeros,
    demoMode,
    backgroundColor,
    showActivationMessage
  };
}
export {
  DonationTrainOverlay,
  DonationTrainOverlayBackgroundColors,
  getDonationTrainConfigFromURL,
  toDonationTrainOverlayBackgroundColor
};
//# sourceMappingURL=donation-train.overlay.js.map
